import styled from "@emotion/styled";
import type { FC } from "react";
import { useBloc as useBlocNext } from "@blac/react";
import { IconClose } from "src/constants/icons";
import { iconUrlClipboardCheck } from "src/constants/iconUrls";
import { APP_BREAKPOINT } from "src/constants/layout";
import translate from "src/lib/translate";
import ObservationBundleBloc from "src/state/ObservationBundleBloc/ObservationBundleBloc";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import Translate from "src/ui/components/Translate/Translate";

//#region Styled components
const Card = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--color-cream-darker);
  border: 1px solid rgba(170, 167, 160, 0.5);
`;

const cardTextBase = `
  font-family: "STK Bureau";
  font-size: 14px;
  font-style: normal;
  line-height: 140%;
  letter-spacing: -0.28px;
  font-feature-settings: 'liga' off, 'clig' off;
`;

const TextBlock = styled.div`
  padding-right: 1.25rem;
`;

const Title = styled.span`
  ${cardTextBase}
  font-weight: 500;
`;

const Description = styled.span`
  ${cardTextBase}
  font-weight: 400;
`;

const ImageContainer = styled.div`
  width: 4rem;
  height: 4rem;
  margin-right: -1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DismissButton = styled.button`
  position: absolute;
  top: 7px;
  right: 7.5px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 200px;
  background: rgba(33, 33, 33, 0.16);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  transition: background 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  @media (pointer: fine) {
    &:hover {
      background: rgba(33, 33, 33, 0.28);
    }
  }

  &:active {
    background: rgba(33, 33, 33, 0.36);
  }

  svg {
    display: block;
    width: 12px;
    height: 12px;
  }

  svg path {
    stroke: var(--color-white);
    stroke-width: 1.75;
  }

  @media (min-width: ${APP_BREAKPOINT}px) {
    top: 11px;
    right: 11px;
    width: 32px;
    height: 32px;

    svg {
      width: 16px;
      height: 16px;
    }

    svg path {
      stroke-width: 1.3125;
    }
  }
`;
//#endregion

const LabImportBanner: FC = () => {
  const [{ observationBundles }, { hasImportedBundle }] = useBlocNext(
    ObservationBundleBloc
  );
  // Only the dismissal decides whether this banner shows. It used to hide while
  // preferences were writing, which meant an unrelated write — hiding a chart —
  // took the banner down with it. `isLabsImportBannerDismissed` is a getter, so
  // the key it reads has to be named as a dependency to repaint on dismiss.
  const [, { updateUserPreferencesOptimistic, isLabsImportBannerDismissed }] =
    useBlocNext(UserPreferencesCubit, {
      dependencySelector: (s) => [
        s[UserPreferenceKeys.labsImportBannerDismissed]
      ]
    });

  const show =
    !isLabsImportBannerDismissed && hasImportedBundle(observationBundles ?? []);

  if (!show) {
    return null;
  }

  const dismiss = (): void => {
    void updateUserPreferencesOptimistic({
      [UserPreferenceKeys.labsImportBannerDismissed]: true
    });
  };

  return (
    <Card role="article">
      <TextBlock>
        <Title>
          <Translate msg="labResults.importBanner.title" />
        </Title>
        <Description> - </Description>
        <Description>
          <Translate msg="labResults.importBanner.text" />
        </Description>
      </TextBlock>
      <ImageContainer>
        <img src={iconUrlClipboardCheck} alt="" aria-hidden="true" />
      </ImageContainer>
      <DismissButton
        type="button"
        aria-label={translate("labResults.importBanner.dismiss")}
        onClick={dismiss}
      >
        <IconClose />
      </DismissButton>
    </Card>
  );
};

export default LabImportBanner;
